import { SyntheticEvent } from 'react';
import { Autocomplete, MenuItem, TextField } from '@mui/material';
import axios from 'src/utils/axios';
import useAuth from 'src/hooks/useAuth';
import useLocales from 'src/hooks/useLocales';
import { useAppDispatch, useAppSelector } from 'src/redux/hook';
import { useGetOrganizersQuery } from 'src/redux/api/userAPI';
import { userOrganizersActions } from 'src/redux/user-organizers';
import { userRolesActions } from 'src/redux/user-roles';
import { UserOrganizerData } from 'src/@types/user';
import { organizerSettingsActions } from 'src/redux/organizer-settings';
import { PaymentMethod } from 'src/@types/box-office';
import { VirtualizedAutocompleteListbox } from 'src/components/VirtualizedAutocompleteListbox';

type Props = {
  isCurrentOrganizerDeleted?: boolean;
  onClose?: VoidFunction;
};

export default function OrganizerSelect({ isCurrentOrganizerDeleted, onClose }: Props) {
  const { translate } = useLocales();

  const { user } = useAuth();

  const dispatch = useAppDispatch();

  const { setCurrentUserOrganizer } = userOrganizersActions;
  const { setCurrentUserRole } = userRolesActions;
  const { setOrganizerSettings } = organizerSettingsActions;

  const currentOrganizer = useAppSelector((state) => state.userOrganizers.currentUserOrganizer);

  const { data: organizers, isLoading, error } = useGetOrganizersQuery(user?.id);

  const selectedOrganizer = organizers?.find((organizer) => organizer.id === currentOrganizer?.id)
    ? currentOrganizer
    : null;

  const handleOrganizer = async (selectedOrganizer: UserOrganizerData | null) => {
    if (selectedOrganizer) {
      try {
        const { data: rolesByOrganizer } = await axios.get(
          `user/${user?.id}/organizer/${selectedOrganizer.id}/role`
        );

        const [role] = rolesByOrganizer;

        dispatch(setCurrentUserOrganizer(selectedOrganizer));
        dispatch(setCurrentUserRole(role));

        localStorage.setItem('currentOrganizerId', selectedOrganizer.id.toString());
        localStorage.setItem('currentAccountId', role.id.toString());

        const { data: organizerSettings } = await axios.get(
          `/organizer/${selectedOrganizer.id}/settings`
        );

        dispatch(
          setOrganizerSettings({
            settingsId: organizerSettings?.id ?? null,
            paymentMethods: organizerSettings?.enabledOfflinePaymentMethods?.split(',') ?? [
              PaymentMethod.CASH,
              PaymentMethod.CARD,
            ],
          })
        );
      } catch (error) {
        console.error(error);
      }

      onClose && onClose();
    }
  };

  return (
    <>
      {!!organizers?.length && !isLoading && !error && (
        <Autocomplete
          fullWidth
          size="small"
          autoHighlight
          disabled={organizers.length === 1 && !isCurrentOrganizerDeleted}
          options={organizers}
          value={selectedOrganizer}
          getOptionLabel={(option) =>
            `${option.name} ${option.companyName ? `(${option.companyName})` : ''}`
          }
          data-testid="organizer-select"
          onChange={(_e: SyntheticEvent<Element, Event>, value: UserOrganizerData | null) =>
            handleOrganizer(value)
          }
          slotProps={{
            listbox: {
              component: VirtualizedAutocompleteListbox,
            },
          }}
          renderOption={(props, option) => (
            <MenuItem {...props} key={option.id}>
              {`${option.name} ${option.companyName ? `(${option.companyName})` : ''}`}
            </MenuItem>
          )}
          renderInput={(params) => (
            <TextField {...params} label={translate('organizerSelectLabel')} />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />
      )}
    </>
  );
}
